import { css, Inline, styled } from 'canoe';
import React, { useRef } from 'react';
import EventListener from 'react-event-listener';

import { ComponentsColumns, ScrollableColumn, StaticColumn } from 'craft/components/ComponentsTab';
import { ComponentsSectionSidebar } from 'craft/components/ComponentsTab/ComponentsSectionsSidebar';
import { usePaletteAppearence } from 'craft/state';
import { PaletteAppearance } from 'craft/state/menus/reducer';
import { Tab } from 'craft/types';

import { ComponentsSearch } from './ComponentsSearch';
import { ToggleAppearance } from './ToggleAppearance';

interface Props {
  onClose: () => void;
}

const PanelContainer = styled(Inline)`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    border-bottom: 1px solid ${theme.colors.GlitterGrayD10};
    padding: ${theme.spaces.xs};
    margin-bottom: ${theme.spaces.xs};
  `}
`;

const PanelTitle = styled.div`
  ${({ theme }) => css`
    margin: 0;
    text-transform: uppercase;
    font-size: 14px;
    font-weight: 400;
    color: ${theme.colors.OnyxGrayL20};
  `}
`;

const ComponentWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    padding: 0 ${theme.spaces.xs} ${theme.spaces.xs} ${theme.spaces.xs};
    overflow-y: auto;
    user-select: none;
  `}
`;

const StyledPanel = styled.div<any>`
  ${({ theme, isOpen, isPinned, widthOverride }) => css`
    position: absolute;
    top: 0;
    left: 62px;
    bottom: 32px;
    width: 0;
    display: flex;

    background-color: ${theme.colors.GlitterGrayL30};

    transition: ${theme.transitions.default()};

    ${isOpen &&
    css`
      width: ${isPinned ? 240 : widthOverride || 327}px;
    `}

    ${isPinned &&
    css`
      bottom: 0;
    `}
  `}
`;

const DragPanel = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

interface Props {
  onClose: () => void;
  isPinned?: boolean;
  tab?: Tab;
}

const DRAG_CLASS = 'drag-panel';

export const Panel = React.memo(({ onClose, tab }: Props) => {
  const palette = useRef<HTMLDivElement>(null);
  const pinnedAppearance = usePaletteAppearence();

  const isPinned = pinnedAppearance === PaletteAppearance.PINNED;
  const isLayers = tab?.label === 'Layers';

  const onDragStart = (e: any) => {
    e.stopPropagation();
  };

  const onDragLeave = (event: DragEvent) => {
    const isFromWithinPanel =
      !!event.relatedTarget && !!(event.relatedTarget as Element).closest(`.${DRAG_CLASS}`);
    if (!isFromWithinPanel) {
      onClose();
    }
  };

  const Component = tab?.Component || (() => null);

  return (
    <StyledPanel
      data-cy="palette-panel"
      isOpen={!!tab}
      isPinned={isPinned && isLayers}
      widthOverride={tab?.label === 'Components' ? 472 : undefined}
      ref={palette}
    >
      {palette.current && tab?.label !== 'Layers' ? (
        <EventListener
          target={palette.current}
          onDragStart={onDragStart}
          onDragLeave={onDragLeave}
        />
      ) : null}
      <DragPanel className={DRAG_CLASS} data-cy="palette-tab-panel">
        {tab?.label !== 'Components' ? (
          <>
            <PanelContainer align="center" justify="space-between" space="none">
              <PanelTitle>{tab?.label}</PanelTitle>
              {isLayers && <ToggleAppearance />}
            </PanelContainer>
            <ComponentWrapper>
              <Component />
            </ComponentWrapper>
          </>
        ) : (
          <>
            <ComponentsSearch />
            <ComponentsColumns>
              <StaticColumn>
                <ComponentsSectionSidebar />
              </StaticColumn>
              <ScrollableColumn>
                <ComponentWrapper id="components-scrollable-container">
                  <Component />
                </ComponentWrapper>
              </ScrollableColumn>
            </ComponentsColumns>
          </>
        )}
      </DragPanel>
    </StyledPanel>
  );
});

Panel.displayName = 'Panel';
